// src/services/authService.js
import { apiRequest } from './api';

export const authService = {
    // Web admin login
    login: async (username, password) => {
        try {
            const response = await apiRequest.post('/web/login', {
                username,
                password
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Login failed');
        }
    },

    // Organization registration with email verification and new schema
    register: async (orgData) => {
        try {
            const response = await apiRequest.post('/web/register', {
                username: orgData.username,
                email: orgData.email,
                password: orgData.password,
                full_name: orgData.full_name, // Admin's full name
                org_name: orgData.org_name,
                description: orgData.description,
                org_category_id: orgData.org_category_id,
                country_id: orgData.country_id, // Backend derives org country_code from this
                phone: orgData.phone
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Registration failed');
        }
    },

    // Get organization categories for registration
    getOrganizationCategories: async (search = '') => {
        try {
            const params = search ? `?search=${encodeURIComponent(search)}` : '';
            const response = await apiRequest.get(`/organization-categories${params}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch organization categories');
        }
    },

    // Get countries with dial codes
    getCountriesWithDialCodes: async (search = '') => {
        try {
            const params = search ? `?search=${encodeURIComponent(search)}` : '';
            const response = await apiRequest.get(`/countries-dialcodes${params}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch countries');
        }
    },

    // Verify email with token
    verifyEmail: async (token) => {
        try {
            const response = await apiRequest.get(`/verify-email?token=${token}`);

            // Check if the response indicates success
            if (response.data && response.data.status === 'success') {
                return {
                    success: true,
                    message: response.data.message || 'Email verified successfully!'
                };
            } else {
                // Handle unexpected response format
                throw new Error(response.data?.message || 'Unexpected response format');
            }
        } catch (error) {
            // Better error extraction from response
            let errorMessage = 'Email verification failed';

            if (error.response) {
                // Server responded with error status
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.statusText) {
                    errorMessage = error.response.statusText;
                }
            } else if (error.message) {
                // Network or other error
                errorMessage = error.message;
            }

            throw new Error(errorMessage);
        }
    },
    forgotPassword: async (email) => {
        try {
            const response = await apiRequest.post('/forgot-password', { email });
            // Return the full response data including message
            return {
                success: true,
                message: response.data.message || 'Password reset link sent to your email'
            };
        } catch (error) {
            throw new Error(error.message || 'Password reset request failed');
        }
    },
    resetPassword: async (token, newPassword) => {
        try {
            const response = await apiRequest.post('/reset-password', {
                token,
                password: newPassword
            });
            // The API returns message in response.data.message, not response.data.data.message
            return {
                success: true,
                message: response.data.message || 'Password reset successfully'
            };
        } catch (error) {
            if (error.response && error.response.data && error.response.data.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error(error.message || 'Password reset failed');
        }
    },

    // Resend verification email
    resendVerification: async (email) => {
        try {
            const response = await apiRequest.post('/resend-verification', {
                email
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to resend verification email');
        }
    },

    // Mobile device authentication (for mobile sync)
    deviceAuth: async (username, password, deviceId) => {
        try {
            const response = await apiRequest.post('/auth', {
                username,
                password,
                device_id: deviceId
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Device authentication failed');
        }
    },

    // Validate token - updated method name for consistency with AuthContext
    verifyToken: async (token) => {
        try {
            const response = await apiRequest.get('/dashboard/stats', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return { valid: response.data.status === 'success' };
        } catch (error) {
            return { valid: false, error: error.message };
        }
    },

    // Legacy method for backward compatibility
    validateToken: async (token) => {
        const result = await authService.verifyToken(token);
        return result.valid;
    },

    // Refresh token (if your backend supports it)
    refreshToken: async (refreshToken) => {
        try {
            const response = await apiRequest.post('/auth/refresh', {
                refresh_token: refreshToken
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Token refresh failed');
        }
    },

    // Logout (clear server-side sessions if needed)
    logout: async () => {
        try {
            await apiRequest.post('/auth/logout');
        } catch (error) {
            // Logout should proceed even if server request fails
            console.warn('Logout request failed:', error.message);
        }
    },

    // Change password (authenticated user)
    changePassword: async (currentPassword, newPassword, token) => {
        try {
            const response = await apiRequest.post('/change-password', {
                current_password: currentPassword,
                new_password: newPassword
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Password change failed');
        }
    },


    // Update user profile
    updateProfile: async (profileData, token) => {
        try {
            const response = await apiRequest.put('/profile/update', profileData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Profile update failed');
        }
    },

    // Get user profile
    getUserProfile: async (token) => {
        try {
            const response = await apiRequest.get('/profile', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch profile');
        }
    },

    // ============================================
    // CORE ADMIN METHODS
    // ============================================

    // Get organizations (core admin only) - Updated for new schema
    getOrganizations: async (token, page = 1, limit = 20, filters = {}) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...filters
            });

            const response = await apiRequest.get(`/organizations?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch organizations');
        }
    },


    // Create organization (core admin only) - Updated for new schema
    createOrganization: async (orgData, token) => {
        try {
            const response = await apiRequest.post('/organizations', {
                name: orgData.name,
                description: orgData.description,
                org_category_id: orgData.org_category_id,
                admin_username: orgData.admin_username,
                admin_email: orgData.admin_email,
                admin_phone: orgData.admin_phone
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create organization');
        }
    },

    // Approve/reject organization
    approveOrganization: async (orgId, action, token) => {
        try {
            const response = await apiRequest.post('/organizations/approve', {
                org_id: orgId,
                action: action
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || `Failed to ${action} organization`);
        }
    },

    // Create app admin
    createAppAdmin: async (adminData, token) => {
        try {
            const response = await apiRequest.post('/app-admins', adminData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create App Admin');
        }
    },

    // ============================================
    // STAFF MANAGEMENT
    // ============================================

    // Get staff members
    getStaff: async (token, page = 1, limit = 20, filters = {}) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...filters
            });

            const response = await apiRequest.get(`/staff?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch staff');
        }
    },

    // Create staff member
    createStaff: async (staffData, token) => {
        try {
            const response = await apiRequest.post('/staff', staffData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create staff member');
        }
    },

    // Get users
    getUsers: async (token, page = 1, limit = 20, filters = {}) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...filters
            });

            const response = await apiRequest.get(`/users?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch users');
        }
    },

    // Create user
    createUser: async (userData, token) => {
        try {
            const response = await apiRequest.post('/users', userData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create user');
        }
    },

    // ============================================
    // ATTENDANCE MANAGEMENT
    // ============================================

    // Get attendance records
    getAttendance: async (token, page = 1, limit = 20, filters = {}) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...filters
            });

            const response = await apiRequest.get(`/attendance?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch attendance');
        }
    },

    // Mark attendance (mobile sync)
    markAttendance: async (attendanceData, token) => {
        try {
            const response = await apiRequest.post('/attendance/mark', attendanceData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to mark attendance');
        }
    },

    // ============================================
    // LEAVE MANAGEMENT
    // ============================================

    // Get leave requests
    getLeaveRequests: async (token, page = 1, limit = 20, filters = {}) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...filters
            });

            const response = await apiRequest.get(`/leaves?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch leave requests');
        }
    },

    // Apply for leave
    applyLeave: async (leaveData, token) => {
        try {
            const response = await apiRequest.post('/leaves/apply', leaveData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to apply for leave');
        }
    },

    // Approve/reject leave
    approveLeave: async (leaveId, action, comments, token) => {
        try {
            const response = await apiRequest.post('/leaves/approve', {
                leave_request_id: leaveId,
                status: action,
                comments: comments
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || `Failed to ${action} leave request`);
        }
    },

    // Get leave types
    getLeaveTypes: async (token, premisesId = '') => {
        try {
            const params = premisesId ? `?premises_id=${premisesId}` : '';

            const response = await apiRequest.get(`/leaves/types${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch leave types');
        }
    },

    // ============================================
    // PREMISES MANAGEMENT
    // ============================================

    // Get premises
    getPremises: async (token) => {
        try {
            const response = await apiRequest.get('/premises', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch premises');
        }
    },

    // Create premises
    createPremises: async (premisesData, token) => {
        try {
            const response = await apiRequest.post('/premises', premisesData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create premises');
        }
    },

    // Get shifts
    getShifts: async (token, premisesId = '') => {
        try {
            const params = premisesId ? `?premises_id=${premisesId}` : '';

            const response = await apiRequest.get(`/shifts${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch shifts');
        }
    },

    // ============================================
    // REPORTING
    // ============================================

    // Get dashboard statistics
    getDashboardStats: async (token) => {
        try {
            const response = await apiRequest.get('/dashboard/stats', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch dashboard stats');
        }
    },

    // Get attendance report
    getAttendanceReport: async (token, filters = {}) => {
        try {
            const params = new URLSearchParams(filters);

            const response = await apiRequest.get(`/reports/attendance?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch attendance report');
        }
    },

    // Export attendance report as CSV
    exportAttendanceReport: async (token, filters = {}) => {
        try {
            const params = new URLSearchParams({
                ...filters,
                export: 'csv'
            });

            const response = await apiRequest.get(`/reports/attendance?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Handle CSV download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `attendance_report_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true, message: 'Report downloaded successfully' };
        } catch (error) {
            throw new Error(error.message || 'Failed to export report');
        }
    },

    // ============================================
    // UTILITY METHODS
    // ============================================

    // Get countries (legacy method - kept for backward compatibility)
    getCountries: async () => {
        try {
            const response = await apiRequest.get('/countries');
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch countries');
        }
    },

    // Get cities by country
    getCities: async (countryId) => {
        try {
            const response = await apiRequest.get(`/cities?country_id=${countryId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch cities');
        }
    },

    // Bulk upload
    bulkUpload: async (formData, token) => {
        try {
            const response = await apiRequest.post('/bulk/upload', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Don't set Content-Type for FormData
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Bulk upload failed');
        }
    },

    // Get bulk upload status
    getBulkUploadStatus: async (batchId, token) => {
        try {
            const response = await apiRequest.get(`/bulk/status?batch_id=${batchId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch upload status');
        }
    },

    // ============================================
    // PAYMENT METHODS
    // ============================================

    // Get payment gateways
    getPaymentGateways: async (countryCode = '') => {
        try {
            const params = countryCode ? `?country_code=${countryCode}` : '';

            const response = await apiRequest.get(`/payments/gateways${params}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch payment gateways');
        }
    },

    // Get payment plans
    getPaymentPlans: async (currency = 'USD') => {
        try {
            const response = await apiRequest.get(`/payments/plans?currency=${currency}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch payment plans');
        }
    },

    // Initiate payment
    initiatePayment: async (paymentData, token) => {
        try {
            const response = await apiRequest.post('/payments/initiate', paymentData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to initiate payment');
        }
    },

    // Verify payment
    verifyPayment: async (verificationData, token) => {
        try {
            const response = await apiRequest.post('/payments/verify', verificationData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Payment verification failed');
        }
    }
};
